import {
  approverFor,
  type Approval,
  type ApprovalStage,
  type FieldDef,
  type SubmissionStage,
  type SubmissionType,
} from '@jfh/intake-shared';
import type { DecisionEmail } from '../lib/api.js';
import { ukDayTime } from './fieldDisplay.js';

/**
 * The application detail's sentences (F10 P3): what approving and declining
 * do, who decided, and the toasts after a decision.
 */

/** "elvin.hughes@…" → "Elvin Hughes" — staff emails are first.last. */
export function personName(email: string): string {
  const local = email.split('@')[0] ?? email;
  if (!email.includes('@')) return email;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const quoted = (e: DecisionEmail) => `‘${e.name}’`;

/** "No email sent: ‘…’ is switched off in Settings." */
export function emailOffLine(e: DecisionEmail): string {
  return `No email sent: ${quoted(e)} is switched off in Settings.`;
}

/** The approve button's words. */
export function approveLabel(type: SubmissionType, stage: SubmissionStage): string {
  if (type === 'credit_account') return stage === 'pending_credit' ? 'Approve credit' : 'Approve trade account';
  return 'Approve';
}

/** What approving does, on the left of the decision bar. */
export function approveConsequence(type: SubmissionType, acting: ApprovalStage, email: DecisionEmail | null): string {
  const sends = email?.on ? ` and sends ${quoted(email)}` : '';
  const off = email && !email.on ? ` ${emailOffLine(email)}` : '';
  if (acting === 'accounts') return `Approving adds the credit terms in iQ${sends}.${off}`;
  if (acting === 'supplier' || type === 'supplier_account') return `Approving sets the supplier up in iQ${sends}.${off}`;
  if (type === 'credit_account') {
    return `Approving sets the trade account up in iQ${email?.on ? `, sends ${quoted(email)}` : ''} and passes credit to Accounts.${off}`;
  }
  return `Approving sets the account up in iQ${sends}.${off}`;
}

/** The decline dialog's title and what follows. */
export function declineDialog(
  type: SubmissionType,
  acting: ApprovalStage,
  applicant: string,
  email: DecisionEmail | null,
): { title: string; lines: string[] } {
  const credit = acting === 'accounts';
  const lines: string[] = [];
  if (credit) lines.push('The trade account stays open for pro-forma orders. No credit terms are added in iQ.');
  else if (type === 'credit_account') lines.push('No account is created in iQ, and credit doesn’t go to Accounts.');
  else if (acting === 'supplier' || type === 'supplier_account') lines.push('The supplier isn’t set up in iQ.');
  else lines.push('No account is created in iQ.');
  if (email) lines.push(email.on ? `The applicant is sent ${quoted(email)}.` : emailOffLine(email));
  lines.push('Your reason stays with JFH. It isn’t sent to the applicant.');
  return { title: credit ? `Decline credit for ${applicant}?` : `Decline ${applicant}?`, lines };
}

/**
 * Who decided, for a finished application: "Credit declined by Grace Okafor
 * on Fri 16 Oct, 10:15: ‘…’ The trade account stays open for pro-forma orders."
 */
export function decidedSentence(type: SubmissionType, stage: SubmissionStage, approvals: Approval[]): string | null {
  if (type === 'lead') return 'Website enquiries go straight to iQ. Nobody needs to approve them.';
  if (approverFor(type, stage)) return null;
  const last = [...approvals].sort((a, b) => a.decidedAt.localeCompare(b.decidedAt)).at(-1);
  if (!last) return null;
  const credit = last.stage === 'accounts';
  const verb =
    last.decision === 'approve' ? (credit ? 'Credit approved' : 'Approved') : credit ? 'Credit declined' : 'Declined';
  let s = `${verb} by ${personName(last.actor)} on ${ukDayTime(last.decidedAt)}`;
  s += last.reason ? `: ‘${last.reason}’` : '.';
  if (credit && last.decision === 'decline') s += ' The trade account stays open for pro-forma orders.';
  return s;
}

/** What iQ did in the decision, from the newest iQ post. */
export interface IqAfter {
  ok: boolean;
  stubbed: boolean;
  code: string | null;
}

/** The toast after approving: names the iQ code and the email sent. */
export function approvedToast(
  type: SubmissionType,
  acting: ApprovalStage,
  iq: IqAfter | null,
  email: DecisionEmail | null,
): { title: string; body?: string; bad?: boolean } {
  const credit = acting === 'accounts';
  const what = credit
    ? 'add the credit terms'
    : acting === 'supplier' || type === 'supplier_account'
      ? 'set the supplier up'
      : 'set the customer up';
  if (!iq || (!iq.ok && !iq.stubbed)) {
    return {
      title: `Approved, but iQ couldn’t ${what}`,
      body: 'No email was sent. The problem and a Send to iQ again button are on the application.',
      bad: true,
    };
  }
  const code = iq.code ? ` ${iq.code}` : '';
  const title = iq.stubbed
    ? 'Approved. iQ isn’t connected here, so nothing went to iQ.'
    : credit
      ? `Credit terms added to${code || ' the account'} in iQ.`
      : acting === 'supplier' || type === 'supplier_account'
        ? `Supplier${code} set up in iQ.`
        : `${type === 'credit_account' ? 'Trade account' : 'Account'}${code} set up in iQ.`;
  const mail = email ? (email.on ? `${quoted(email)} sent to the applicant.` : emailOffLine(email)) : '';
  const next = type === 'credit_account' && !credit ? ' Credit is now with Accounts.' : '';
  return { title, body: `${mail}${next}`.trim() || undefined };
}

/** The toast after declining. */
export function declinedToast(applicant: string, acting: ApprovalStage, email: DecisionEmail | null): { title: string; body?: string } {
  return {
    title: acting === 'accounts' ? `Credit declined for ${applicant}` : `${applicant} declined`,
    body: email ? (email.on ? `${quoted(email)} sent to the applicant.` : emailOffLine(email)) : undefined,
  };
}

export interface AnswerGroup {
  title: string | null;
  fields: FieldDef[];
}

/** The applicant's questions under the form's step headings. */
export function answerGroups(customerSchema: FieldDef[]): AnswerGroup[] {
  const groups: AnswerGroup[] = [];
  let current: AnswerGroup = { title: null, fields: [] };
  for (const f of customerSchema) {
    if (f.type === 'heading') {
      if (current.fields.length) groups.push(current);
      current = { title: f.label, fields: [] };
    } else {
      current.fields.push(f);
    }
  }
  if (current.fields.length) groups.push(current);
  return groups;
}
